import PropTypes from 'prop-types';

import { TOOL_TYPE } from 'core/chatbot/utils/parsers/ToolType';
import Tooltip from 'core/modules/atoms/Tooltip/Tooltip';
import Markdown from 'core/modules/components/Markdown/Markdown';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAnalytics from '../../../../hooks/useAnalytics';
import { EVENT_NAMES } from '../../../constants/eventNames';
import { useAIChatConversation } from '../../../contexts/AIChatConversation/useAIChatConversation';
import messageContentParser from '../../../utils/MessageContentParser';
import StatusModal from '../../../views/ChatbotPracticeView/StatusModal';
import ChatContentReferences from '../ChatContentReferences/ChatContentReferences';
import ChatD3Modal from '../ChatD3Modal/ChatD3Modal';
import ChatD3VisualizationCard from '../ChatD3VisualizationCard/ChatD3VisualizationCard';
import ChatIconButton from '../ChatIconButton';
import ChatImageModal from '../ChatImageModal/ChatImageModal';
import ChatMermaidCard from '../ChatMermaidCard/ChatMermaidCard';
import ChatMermaidModal from '../ChatMermaidModal/ChatMermaidModal';
import FlaggedMessageBubbleTags from '../FlaggedMessageBubbleTags/FlaggedMessageBubbleTags';
import FlagReportModal from '../FlagReportModal/FlagReportModal';
import ToolIndicator from '../ToolIndicator/ToolIndicator';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './ChatMessage.scss';
import { useMessageActions } from './useMessageActions';

function ChatMessage({ message, className, activeTools, highlighted, onMessageFlagged, readOnly }) {
  const { t } = useTranslation(['chatbot']);
  const { trackEvent } = useAnalytics();
  const { currentConversationId } = useAIChatConversation();
  const isBot = message.sender === 'bot';
  const { isStreaming, isError, justPosted, isFlagged: isFlaggedFromApi } = message;
  const [modalData, setModalData] = useState({ isOpen: false, url: '', title: '' });
  const [mermaidModalData, setMermaidModalData] = useState({ isOpen: false, mermaidCode: '', title: '' });
  const [imageModalData, setImageModalData] = useState({ isOpen: false, imageSrc: '', imageName: '', title: '' });

  // Track if this message was flagged during this session (for immediate UI update)
  const [localFlagged, setLocalFlagged] = useState(false);
  const isFlagged = isFlaggedFromApi || localFlagged;

  // Parse message content using the modular parser system
  const parseMessageContent = useCallback((content) => {
    return messageContentParser.parseContent(content);
  }, []);

  // Handle opening the D3 visualization modal
  const handleOpenVisualization = useCallback(
    (url, title, type) => {
      setModalData({ isOpen: true, url, title });

      // Track analytics event for visualization opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type,
        });
      } catch (analyticsError) {
        console.error('Failed to track visualization opened event:', analyticsError);
      }
    },
    [trackEvent]
  );

  // Handle closing the D3 modal
  const handleCloseModal = useCallback(() => {
    setModalData({ isOpen: false, url: '', title: '' });
  }, []);

  // Handle opening the Mermaid diagram modal
  const handleOpenMermaidDiagram = useCallback(
    (mermaidCode, title) => {
      setMermaidModalData({ isOpen: true, mermaidCode, title });

      // Track analytics event for mermaid diagram opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type: TOOL_TYPE.MERMAID_DIAGRAM,
        });
      } catch (analyticsError) {
        console.error('Failed to track mermaid diagram opened event:', analyticsError);
      }
    },
    [trackEvent]
  );

  // Handle closing the Mermaid modal
  const handleCloseMermaidModal = useCallback(() => {
    setMermaidModalData({ isOpen: false, mermaidCode: '', title: '' });
  }, []);

  // Handle opening the image modal
  const handleOpenImageModal = useCallback(
    (imageSrc, imageName) => {
      setImageModalData({ isOpen: true, imageSrc, imageName, title: imageName || t('chatbot:Image') });

      // Track analytics event for image opened
      try {
        trackEvent(EVENT_NAMES.ARTIFACT_OPENED, {
          type: TOOL_TYPE.IMAGE,
        });
      } catch (analyticsError) {
        console.error('Failed to track image opened event:', analyticsError);
      }
    },
    [trackEvent, t]
  );

  // Handle closing the image modal
  const handleCloseImageModal = useCallback(() => {
    setImageModalData({ isOpen: false, imageSrc: '', imageName: '', title: '' });
  }, []);

  // Handle successful flag - update local state for immediate UI feedback
  const handleMessageFlagged = useCallback(
    (messageId, response) => {
      setLocalFlagged(true);
      if (onMessageFlagged) {
        onMessageFlagged(messageId, response);
      }
    },
    [onMessageFlagged]
  );

  // Use the message actions hook
  const {
    handleCopyMessage,
    showCopySuccess,
    handlePlayAudio,
    isPlayingAudio,
    isLoadingAudio,
    isFlagModalOpen,
    isFlagging,
    flagStatusModal,
    handleOpenFlagModal,
    handleCloseFlagModal,
    handleCloseFlagStatusModal,
    handleSubmitFlag,
    feedback,
    handleFeedback,
  } = useMessageActions({
    message,
    onMessageFlagged: handleMessageFlagged,
  });

  // Parse the message content
  const parsedContent = useMemo(() => parseMessageContent(message.text), [message.text, parseMessageContent]);

  // Extraer referencias de contenido para mostrar en el footer
  const contentReferences = useMemo(() => {
    const references = [];
    parsedContent.forEach((part) => {
      if (part.type === TOOL_TYPE.CONTENT_REFERENCES && part.references) {
        references.push(...part.references);
      }
    });
    return references;
  }, [parsedContent]);

  // Si es un mensaje en streaming de un bot, solo mostrar el indicador de escritura
  if (isBot && isStreaming && !message.text && !activeTools?.length) {
    return (
      <div className={`chat-message ${className || ''} chat-message--streaming`}>
        <div className="chat-bubble chat-bubble--bot">
          <div className="chat-bubble__avatar" />
          <div className="chat-bubble__content">
            <TypingIndicator />
          </div>
        </div>
      </div>
    );
  }

  // If streaming with active tools but no text yet, render only tool indicators
  if (isBot && isStreaming && !message.text && activeTools && activeTools.length > 0) {
    return (
      <div className={`chat-message ${className || ''}`}>
        <div className="chat-bubble chat-bubble--bot">
          <div className="chat-bubble__avatar" />
          <div className="chat-bubble__content">
            <div className="chat-bubble__tool-indicators">
              {activeTools.map((event) => (
                <ToolIndicator key={event.id} toolEvent={event} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-message-id={message.id}
      className={`chat-message ${isError ? 'chat-message--error' : ''} ${highlighted ? 'chat-message--highlighted' : ''} ${className || ''}`}
    >
      <div
        className={`chat-bubble ${isBot ? 'chat-bubble--bot' : 'chat-bubble--user'} ${justPosted ? 'chat-bubble--just-posted' : ''} ${
          isError ? 'chat-message__bubble--error' : ''
        }`}
      >
        {isBot && <div className={`chat-bubble__avatar ${isError ? 'chat-bubble__avatar--error' : ''}`} />}

        <div className="chat-bubble__content">
          <div className="chat-bubble__message-group">
            <div className="chat-bubble__message">
              <div className="chat-bubble__message-content">
                {isError && <div className="chat-bubble__text">{message.text}</div>}

                {!isError && (
                  <>
                    {/* Display attachments (images) before text content */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="chat-bubble__attachments">
                        {message.attachments.map((attachment, index) => (
                          <div key={index} className="chat-bubble__image">
                            <img
                              src={attachment.url}
                              alt={attachment.fileName || t('Attached image')}
                              draggable={false}
                              onClick={() => handleOpenImageModal(attachment.url, attachment.fileName)}
                              style={{ cursor: 'pointer' }}
                              onError={(e) => {
                                e.target.style.display = 'none'; // Hide broken images
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Keep support for legacy single image uploads */}
                    {!message.attachments && message.image && (
                      <div className="chat-bubble__image">
                        <img
                          src={message.image}
                          alt={message.imageName || t('Uploaded image')}
                          draggable={false}
                          onClick={() => handleOpenImageModal(message.image, message.imageName)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    )}

                    {/* Display text content */}
                    {parsedContent.map((part, index) => {
                      if (part.type === 'text') {
                        return <Markdown key={index}>{part.content}</Markdown>;
                      } else if (
                        part.type === TOOL_TYPE.D3_VISUALIZATION ||
                        part.type === TOOL_TYPE.GEOGEBRA_VISUALIZATION ||
                        part.type === TOOL_TYPE.THREEJS_VISUALIZATION
                      ) {
                        return (
                          <ChatD3VisualizationCard
                            key={index}
                            url={part.url}
                            title={part.title}
                            onOpen={(url, title) => handleOpenVisualization(url, title, part.type)}
                          />
                        );
                      } else if (part.type === TOOL_TYPE.MERMAID_DIAGRAM) {
                        return (
                          <ChatMermaidCard
                            key={index}
                            conversationId={currentConversationId}
                            mermaidCode={part.mermaidCode}
                            messageId={message.id}
                            onOpen={handleOpenMermaidDiagram}
                            title={part.title}
                          />
                        );
                      } else if (part.type === TOOL_TYPE.CONTENT_REFERENCES) {
                        // Las referencias se renderizan en el footer, no inline
                        return null;
                      }
                      return null;
                    })}
                  </>
                )}
              </div>
              <div className="chat-bubble__message-references">
                {/* Mostrar referencias de contenido en el footer si existen */}
                {contentReferences.length > 0 && <ChatContentReferences references={contentReferences} />}
              </div>
            </div>

            {/* Category tags for flagged messages - inside group, below the bubble */}
            {highlighted && message.flagCategories && message.flagCategories.length > 0 && <FlaggedMessageBubbleTags categories={message.flagCategories} />}
          </div>

          {/* Tool indicators - inside content for proper avatar alignment */}
          {activeTools && activeTools.length > 0 && (
            <div className="chat-bubble__tool-indicators">
              {activeTools.map((event) => (
                <ToolIndicator key={event.id} toolEvent={event} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Copy, audio, and flag buttons for bot messages - below bubble on the left */}
      {!readOnly && isBot && !isError && !isStreaming && message.text && (
        <div className="chat-message__actions">
          <Tooltip title={showCopySuccess ? t('Copied!') : t('Copy message')} placement="top" variant="dark" size="small" arrow>
            <ChatIconButton
              ariaLabel={showCopySuccess ? t('Copied!') : t('Copy message')}
              className="chat-message__copy-button"
              disabled={showCopySuccess}
              icon={showCopySuccess ? 'check' : 'copy'}
              onClick={handleCopyMessage}
              size="small"
              variant="default"
            />
          </Tooltip>
          <Tooltip title={isPlayingAudio ? t('Pause audio') : t('Play message')} placement="top" variant="dark" size="small" arrow>
            <ChatIconButton
              ariaLabel={isPlayingAudio ? t('Pause audio') : t('Play message')}
              className={`chat-message__audio-button ${isLoadingAudio ? 'chat-message__audio-button--loading' : ''}`}
              disabled={isLoadingAudio}
              icon={isPlayingAudio ? 'stop' : 'volume-up'}
              onClick={handlePlayAudio}
              size="small"
              variant="default"
            />
          </Tooltip>
          {/* Feedback buttons - thumbs up/down for non-welcome messages */}
          {message.id !== 'welcome-message' && (
            <>
              <Tooltip title={t('Useful response')} placement="top" variant="dark" size="small" arrow>
                <ChatIconButton
                  ariaLabel={t('Useful response')}
                  icon={feedback === 'positive' ? 'thumbsup_fill' : 'thumbsup'}
                  onClick={() => handleFeedback('positive')}
                  size="small"
                  variant="default"
                />
              </Tooltip>
              <Tooltip title={t('Not useful response')} placement="top" variant="dark" size="small" arrow>
                <ChatIconButton
                  ariaLabel={t('Not useful response')}
                  icon={feedback === 'negative' ? 'thumbsdown_fill' : 'thumbsdown'}
                  onClick={() => handleFeedback('negative')}
                  size="small"
                  variant="default"
                />
              </Tooltip>
            </>
          )}
          {/* Hide flag button for simulated welcome message (id='welcome-message') which has no backend ID */}
          {message.id !== 'welcome-message' &&
            (isFlagged ? (
              <ChatIconButton
                ariaLabel={t('Message reported')}
                className="chat-message__flag-button chat-message__flag-button--flagged"
                disabled
                icon="flag"
                size="small"
                variant="default"
              />
            ) : (
              <Tooltip title={t('Report message')} placement="top" variant="dark" size="small" arrow>
                <ChatIconButton
                  ariaLabel={t('Report message')}
                  className="chat-message__flag-button"
                  icon="flag"
                  onClick={handleOpenFlagModal}
                  size="small"
                  variant="default"
                />
              </Tooltip>
            ))}
        </div>
      )}

      <ChatD3Modal isOpen={modalData.isOpen} onClose={handleCloseModal} url={modalData.url} title={modalData.title} />
      <ChatMermaidModal
        conversationId={currentConversationId}
        isOpen={mermaidModalData.isOpen}
        mermaidCode={mermaidModalData.mermaidCode}
        messageId={message.id}
        onClose={handleCloseMermaidModal}
        title={mermaidModalData.title}
      />
      <ChatImageModal
        isOpen={imageModalData.isOpen}
        onClose={handleCloseImageModal}
        imageSrc={imageModalData.imageSrc}
        imageName={imageModalData.imageName}
        title={imageModalData.title}
      />

      {!readOnly && (
        <>
          {/* Flag Report Modal */}
          <FlagReportModal isSubmitting={isFlagging} onClose={handleCloseFlagModal} onSubmit={handleSubmitFlag} open={isFlagModalOpen} />

          {/* Flag Status Modal (Success/Error) */}
          <StatusModal onClose={handleCloseFlagStatusModal} open={flagStatusModal.open} type={flagStatusModal.type} />
        </>
      )}
    </div>
  );
}

ChatMessage.propTypes = {
  activeTools: PropTypes.array,
  className: PropTypes.string,
  highlighted: PropTypes.bool,
  message: PropTypes.shape({
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        contentType: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        fileName: PropTypes.string.isRequired,
        sizeInBytes: PropTypes.number.isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
    flagCategories: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string.isRequired,
        severity: PropTypes.string.isRequired,
      })
    ),
    isFlagged: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.string,
    imageName: PropTypes.string,
    isError: PropTypes.bool,
    isStreaming: PropTypes.bool,
    justPosted: PropTypes.bool,
    sender: PropTypes.oneOf(['user', 'bot']).isRequired,
    text: PropTypes.string.isRequired,
    timestamp: PropTypes.instanceOf(Date).isRequired,
  }).isRequired,
  onMessageFlagged: PropTypes.func,
  readOnly: PropTypes.bool,
};

ChatMessage.defaultProps = {
  activeTools: [],
  className: '',
  highlighted: false,
  onMessageFlagged: undefined,
  readOnly: false,
};

// Memoized so a streaming-bubble update in ChatMessages does not re-render historical rows: those
// rows receive referentially-stable props (a cached row-model + the shared EMPTY_ACTIVE_TOOLS). The
// context subscription on line 30 still re-renders on real Conversation-state changes, which is
// intended — post canvas-split those fire only on genuine conversation changes, not per SSE frame.
export default React.memo(ChatMessage);
